import { useState, useEffect, useContext, useMemo } from "react"
import { AuthContext } from "../context/AuthContext"
import CourierModal from "../components/CourierModal"
import axios from "axios"
import {
  Trash2,
  Edit,
  Truck,
  Phone,
  MapPin,
  Plus,
  Search,
  Package,
  X,
  Settings,
  Save
} from "lucide-react"
import { showToast } from "../utils/toastUtils"

const BASE_URL = process.env.REACT_APP_API_URL;

const AddCourierService = () => {
  const { token } = useContext(AuthContext)
  const [couriers, setCouriers] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCourier, setEditingCourier] = useState(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")

  // Global Settings state
  const [globalThreshold, setGlobalThreshold] = useState("2000")
  const [defaultShippingCost, setDefaultShippingCost] = useState("350")
  const [savingSettings, setSavingSettings] = useState(false)

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [courierToDelete, setCourierToDelete] = useState(null);

  if (token) {
    axios.defaults.headers.common["x-auth-token"] = token
  }

  const fetchCouriersAndSettings = async () => {
    try {
      setLoading(true)
      const [couriersRes, thresholdRes, shippingRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/couriers`),
        axios.get(`${BASE_URL}/api/settings/freeDeliveryThreshold`).catch(() => null),
        axios.get(`${BASE_URL}/api/settings/defaultShippingCost`).catch(() => null)
      ])
      
      setCouriers(couriersRes.data)
      if (thresholdRes && thresholdRes.data && thresholdRes.data.value) {
        setGlobalThreshold(thresholdRes.data.value.toString())
      }
      if (shippingRes && shippingRes.data && shippingRes.data.value) {
        setDefaultShippingCost(shippingRes.data.value.toString())
      }
    } catch (err) {
      showToast("Error", "Failed to fetch data", "error")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCouriersAndSettings()
  }, [])

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true)
      await Promise.all([
        axios.put(`${BASE_URL}/api/settings/freeDeliveryThreshold`, { value: globalThreshold }),
        axios.put(`${BASE_URL}/api/settings/defaultShippingCost`, { value: defaultShippingCost })
      ])
      showToast("Success", "Global settings updated", "success")
    } catch (err) {
      showToast("Error", "Failed to update settings", "error")
    } finally {
      setSavingSettings(false)
    }
  }

  const handleAddCourier = async (data) => {
    try {
      const res = await axios.post(`${BASE_URL}/api/couriers`, data)
      setCouriers([res.data, ...couriers])
      showToast("Success", "Courier added successfully", "success")
      return true
    } catch (err) {
      showToast("Error", "Error adding courier", "error")
      return false
    }
  }

  const handleEditCourier = async (data) => {
    try {
      const res = await axios.put(`${BASE_URL}/api/couriers/${editingCourier._id}`, data)
      setCouriers(couriers.map((c) => (c._id === editingCourier._id ? res.data : c)))
      showToast("Success", "Courier updated successfully", "success")
      return true
    } catch (err) {
      showToast("Error", "Error updating courier", "error")
      return false
    }
  }

  const handleDeleteClick = (courier) => {
    setCourierToDelete(courier);
    setShowDeleteModal(true);
  }

  const confirmDelete = async () => {
    if (!courierToDelete) return;
    try {
      await axios.delete(`${BASE_URL}/api/couriers/${courierToDelete._id}`)
      setCouriers(couriers.filter((c) => c._id !== courierToDelete._id))
      showToast("Success", "Courier deleted", "success")
    } catch {
      showToast("Error", "Error deleting courier", "error")
    } finally {
      setShowDeleteModal(false);
      setCourierToDelete(null);
    }
  }

  const openModal = (courier = null) => {
    setEditingCourier(courier)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingCourier(null)
  }

  const filteredCouriers = useMemo(() => {
    if (!searchTerm) return couriers;
    return couriers.filter(c =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.contact && c.contact.includes(searchTerm))
    );
  }, [couriers, searchTerm]);

  return (
    <div className="flex bg-gray-50 min-h-screen pb-12">
      <div className="flex-1 flex flex-col w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Truck className="w-8 h-8 text-blue-600" /> Delivery Partners
            </h1>
            <p className="mt-2 text-sm text-gray-500">
              Manage your courier services and configure shipping rates by district.
            </p>
          </div>

          <button
            onClick={() => openModal()}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 shadow-sm transition-all hover:shadow-md"
          >
            <Plus className="w-5 h-5" />
            Add New Courier
          </button>
        </div>

        {/* Global Settings Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <Settings className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Global Shipping Settings</h2>
              <p className="text-sm text-gray-500">Configure global fallbacks and free delivery thresholds.</p>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3 w-full md:w-auto mt-4 md:mt-0">
            <div className="flex flex-col gap-1 w-full md:w-auto">
              <span className="text-xs text-gray-500 font-medium ml-1">Free Delivery Threshold</span>
              <div className="relative flex-1 md:w-48">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">Rs.</span>
                <input
                  type="number"
                  value={globalThreshold}
                  onChange={(e) => setGlobalThreshold(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                  placeholder="2000"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1 w-full md:w-auto">
              <span className="text-xs text-gray-500 font-medium ml-1">Default Base Shipping</span>
              <div className="relative flex-1 md:w-48">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">Rs.</span>
                <input
                  type="number"
                  value={defaultShippingCost}
                  onChange={(e) => setDefaultShippingCost(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                  placeholder="350"
                />
              </div>
            </div>
            <div className="flex flex-col justify-end mt-1 md:mt-5">
              <button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 disabled:opacity-70 disabled:cursor-not-allowed transition-all h-[46px]"
              >
                {savingSettings ? "Saving..." : <Save className="w-4 h-4" />}
                {savingSettings ? "" : "Save"}
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-8">
          <div className="relative group max-w-lg">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input
              type="text"
              placeholder="Search couriers..."
              className="w-full rounded-lg border-gray-200 bg-white px-4 py-2.5 pl-10 text-gray-700 placeholder-gray-400 shadow-sm transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none hover:border-blue-300"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Grid Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mb-4"></div>
            <p className="text-gray-500">Loading delivery partners...</p>
          </div>
        ) : filteredCouriers.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="mx-auto h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Truck className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">No couriers found</h3>
            <p className="text-gray-500 mt-2 max-w-sm mx-auto">
              {couriers.length === 0
                ? "Get started by adding your first delivery partner to handle shipments."
                : "Try adjusting your search terms."}
            </p>
            {couriers.length === 0 && (
              <button
                onClick={() => openModal()}
                className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors"
              >
                <Plus className="w-4 h-4" /> Add Courier
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCouriers.map((courier) => {
              // Calculate simple stats for the card
              const feeCount = Object.keys(courier.fees || {}).length;
              const avgFee = feeCount > 0
                ? (Object.values(courier.fees).reduce((a, b) => Number(a) + Number(b), 0) / feeCount).toFixed(0)
                : 0;

              return (
                <div key={courier._id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all group">
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                          <Truck className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{courier.name}</h3>
                          <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full font-medium">Active</span>
                        </div>
                      </div>
                      <div className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => openModal(courier)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(courier)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center text-sm text-gray-600">
                        <Phone className="w-4 h-4 mr-2 text-gray-400" />
                        {courier.contact || "No contact info"}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                        {feeCount} Districts Configured
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Package className="w-4 h-4 mr-2 text-gray-400" />
                        ~Rs. {avgFee} Avg. Shipping Cost
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-6 py-3 border-t border-gray-100 flex justify-between items-center">
                    <span className="text-xs text-gray-500 font-medium">Last updated recently</span>
                    <button
                      onClick={() => openModal(courier)}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                    >
                      Configure Rates →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {isModalOpen && (
        <CourierModal
          onClose={closeModal}
          onSubmit={editingCourier ? handleEditCourier : handleAddCourier}
          initialData={editingCourier}
        />
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Remove Partner?</h2>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-6 font-medium">
              Are you sure you want to remove <strong>{courierToDelete?.name}</strong>?
            </p>
            <p className="text-xs text-gray-500 mb-6 bg-amber-50 p-3 rounded-lg border border-amber-100">
              <span className="font-bold text-amber-700">Warning:</span> Rate configurations for this courier will be permanently lost.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-sm"
              >
                Remove Courier
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  )
}

export default AddCourierService
